/**
 * Patch 35.2b-ii · Video Render Controller
 *
 * Founder/Admin-only entry point for turning an approved
 * ``video_production_kit`` into a rendered MP4. Three lifecycle
 * surfaces are collapsed into a single stateful component so the
 * drawer can drop it in verbatim:
 *
 *  1. IDLE       — button + preflight modal.
 *  2. IN-FLIGHT  — progress card, polling job status every 3s.
 *  3. TERMINAL   — inline ``<video>`` (via ``RenderedVideoPreview``) or
 *                  clear failure state with retry copy.
 *
 * On mount the component reconciles the *server* view of the asset —
 * it calls ``/active-video-job`` so a page refresh mid-render
 * rediscovers the running job instead of showing an empty idle state.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, AlertTriangle, Loader2, PlayCircle, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/shared/ui/button";
import {
  acceptHeroRisk,
  videoRenderActiveJob,
  videoRenderJobStatus,
  videoRenderPreflight,
  videoRenderSubmit,
  type VideoJobStatus,
  type VideoPreflight,
} from "@/entities/ai-studio/api";

const ACTIVE_STATUSES = new Set([
  "queued", "preparing", "rendering", "finalizing",
]);

// ── Patch 37.4.25 — Customer-safe translation of backend blocker
// codes. Never expose raw machine codes (``hero_risky_requires_override``,
// ``near_monochrome``, etc.) to non-founder customers. The dialog
// shows the copy; the toast fallback keeps the code visible to
// founders for diagnostic clarity.
const HERO_BLOCK_COPY =
  "This starting frame can't be used. Choose a different image.";
const HERO_RISK_COPY =
  "This starting frame has limited detail. Rendering may reduce " +
  "video quality, and production capacity may still be consumed " +
  "even if the render fails.";

function translateBlockerCode(code?: string | null): string {
  if (!code) return "Not eligible right now.";
  const blocked = new Set([
    "image_blocked", "near_monochrome", "low_visual_entropy",
    "insufficient_detail", "image_decode_failed",
    "excessive_transparency", "image_too_small", "file_too_small",
    "unsupported_image_format",
  ]);
  if (blocked.has(code)) return HERO_BLOCK_COPY;
  switch (code) {
    case "image_not_approved":
      return "The selected starting image isn't approved yet.";
    case "image_not_current_head":
    case "image_archived":
    case "image_not_rendered":
      return "The selected starting image is no longer current. Choose a new one.";
    case "signed_url_unavailable":
      return "Can't reach the starting image right now. Try again in a minute.";
    case "hero_risky_requires_override":
      return HERO_RISK_COPY;
    case "render_already_active":
      return "A render for this video is already in progress.";
    default:
      return "Not eligible right now.";
  }
}

interface Props {
  workspaceId: string;
  assetId: string;
  /** Founder/Admin gate — hides everything when false. */
  canRender: boolean;
  /** Called once a completed render has produced a v2 asset so the
   *  parent (drawer) can refetch its list. */
  onCompleted?: (completedAssetId: string) => void;
}

export function VideoRenderController({
  workspaceId, assetId, canRender, onCompleted,
}: Props): JSX.Element | null {
  const qc = useQueryClient();
  const [preflight, setPreflight] = useState<VideoPreflight | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);

  // Rediscover an active/completed job from the backend on mount.
  const active = useQuery({
    queryKey: ["video-render-active", workspaceId, assetId],
    queryFn: () => videoRenderActiveJob(workspaceId, assetId),
    enabled: !!workspaceId && !!assetId,
  });

  useEffect(() => {
    if (active.data?.job_id && !jobId) {
      setJobId(active.data.job_id);
    }
  }, [active.data, jobId]);

  // Poll status while the current job is active.
  const jobStatus = useQuery({
    queryKey: ["video-render-status", workspaceId, jobId],
    queryFn: () => videoRenderJobStatus(workspaceId, jobId as string),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data as VideoJobStatus | undefined;
      if (!data) return 3000;
      return ACTIVE_STATUSES.has(data.status) ? 3000 : false;
    },
  });

  // On completion, invalidate the pack-assets query so the drawer
  // refetches and the new v2 slides in.
  useEffect(() => {
    if (
      jobStatus.data?.status === "ready" &&
      jobStatus.data.completed_asset_id
    ) {
      onCompleted?.(jobStatus.data.completed_asset_id);
      void qc.invalidateQueries({ queryKey: ["pack-assets"] });
      void qc.invalidateQueries({ queryKey: ["ops-versions"] });
    }
  }, [jobStatus.data, onCompleted, qc]);

  const preflightMut = useMutation({
    mutationFn: () => videoRenderPreflight(workspaceId, assetId),
    onSuccess: (data) => {
      setPreflight(data);
      setShowConfirm(true);
    },
    onError: () => toast.error("Could not check render eligibility"),
  });

  const submitMut = useMutation({
    mutationFn: (opts?: { risk_override?: string }) =>
      videoRenderSubmit(workspaceId, assetId, opts),
    onSuccess: (data) => {
      setJobId(data.job_id);
      setShowConfirm(false);
      toast.success("Video queued for production");
      void qc.invalidateQueries({ queryKey: ["video-render-active", workspaceId, assetId] });
    },
    onError: (e: unknown) => {
      // Patch 37.4.24-followup / 37.4.25 hardening — translate the
      // backend code into customer-safe copy. Raw machine codes
      // (``hero_risky_requires_override``, ``near_monochrome``, …)
      // must never reach the toast surface. When the code is
      // present, ``translateBlockerCode`` always returns some copy
      // (worst-case ``"Not eligible right now."``). When it's
      // absent we fall through to err.message / the generic label.
      const err = e as { code?: string; message?: string; status?: number };
      toast.error(
        (err?.code ? translateBlockerCode(err.code) : null)
        || err?.message
        || "Could not submit video render",
      );
    },
  });

  // Patch 37.4.25 — Explicit human acceptance for a risky hero image.
  // Persists a durable acceptance keyed to the currently-selected
  // (hero_asset_id, hero_asset_version). Rejected for blocked/safe
  // heroes. On success, we chain the submit call so "Render anyway"
  // is a single user gesture.
  const acceptRiskMut = useMutation({
    mutationFn: (opts: { hero_asset_id: string; hero_asset_version: number }) =>
      acceptHeroRisk(workspaceId, assetId, opts),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["video-render-preflight", workspaceId, assetId],
      });
      submitMut.mutate(undefined);
    },
    onError: (e: unknown) => {
      const err = e as { code?: string; message?: string };
      // Only the raw code reveal to founders; customers see the copy.
      toast.error(translateBlockerCode(err?.code) || err?.message || "Could not accept image risk");
    },
  });

  const activeJob = jobStatus.data ?? active.data ?? null;
  const status = activeJob?.status ?? "";
  const inFlight = ACTIVE_STATUSES.has(status);
  const failed = status === "failed" || status === "needs_attention";
  const cancelled = status === "cancelled";

  const showActionButton = useMemo(
    () => canRender && !inFlight && status !== "ready",
    [canRender, inFlight, status],
  );

  if (!canRender && !inFlight && !activeJob) return null;

  return (
    <div data-testid="video-render-controller" className="space-y-2">
      {showActionButton && (
        <Button
          data-testid="video-render-button"
          size="sm"
          onClick={() => preflightMut.mutate()}
          disabled={preflightMut.isPending || submitMut.isPending}
        >
          {preflightMut.isPending ? (
            <>
              <Loader2 className="mr-1 h-3 w-3 animate-spin" /> Checking…
            </>
          ) : (
            <>
              <Sparkles className="mr-1 h-3 w-3" />
              {failed || cancelled ? "Try video render again" : "Render video"}
            </>
          )}
        </Button>
      )}

      {inFlight && activeJob && (
        <div
          data-testid={`video-render-progress-${status}`}
          className="flex items-center gap-3 rounded-md border border-border/60 bg-secondary/20 p-3 text-xs"
        >
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <div className="flex-1">
            <div data-testid="video-render-progress-stage" className="font-medium">
              {activeJob.progress_stage}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {activeJob.safe_message} · {activeJob.estimated_completion_window}
            </div>
          </div>
        </div>
      )}

      {failed && activeJob && (
        <div
          data-testid="video-render-failed"
          className="flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-500/5 p-3 text-xs"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
          <div className="flex-1">
            <div className="font-medium">
              {status === "needs_attention" ? "Needs a founder look" : "Video didn't finish"}
            </div>
            <div className="text-muted-foreground">{activeJob.safe_message}</div>
          </div>
        </div>
      )}

      {showConfirm && preflight && (
        <div
          data-testid="video-render-confirm"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowConfirm(false)}
        >
          <div
            className="w-full max-w-md space-y-3 rounded-lg border border-border/60 bg-card p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold">
              <PlayCircle className="mr-1 inline h-4 w-4 text-primary" /> Render this video?
            </h3>
            {!preflight.eligible ? (
              <div
                data-testid="video-render-confirm-blocked"
                className="rounded-md border border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-600"
              >
                {translateBlockerCode(preflight.safe_reason_code)}
              </div>
            ) : (
              <>
                {/* Patch 37.4.25 — Hero blocked → red terminal state. */}
                {preflight.hero_risk?.quality_level === "blocked" && (
                  <div
                    data-testid="video-render-hero-blocked"
                    className="rounded-md border border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-600"
                  >
                    {HERO_BLOCK_COPY}
                  </div>
                )}
                {/* Patch 37.4.25 — Hero risky + not accepted → amber warning + Render anyway. */}
                {preflight.hero_risk?.needs_risk_acceptance && (
                  <div
                    data-testid="video-render-hero-risky-warning"
                    className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs"
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                      <div>
                        <div className="font-medium text-amber-700">
                          Limited-detail starting frame
                        </div>
                        <div className="mt-1 text-amber-700/90">
                          {HERO_RISK_COPY}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Patch 37.4.25 — Hero risky + already accepted → footnote. */}
                {preflight.hero_risk?.quality_level === "risky"
                  && preflight.hero_risk.has_accepted_risk && (
                  <div
                    data-testid="video-render-hero-risk-acknowledged"
                    className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-[11px] text-amber-700"
                  >
                    Warning acknowledged — rendering with a limited-detail starting frame.
                  </div>
                )}
                <div className="rounded-md bg-secondary/30 p-3 text-xs">
                  <div>
                    <span className="text-muted-foreground">Service: </span>
                    <span className="font-medium">
                      {preflight.recommended_provider_service}
                    </span>
                  </div>
                  {preflight.estimated_duration && (
                    <div>
                      <span className="text-muted-foreground">Duration: </span>
                      {preflight.estimated_duration}s
                    </div>
                  )}
                  {preflight.estimated_aspect_ratio && (
                    <div>
                      <span className="text-muted-foreground">Aspect: </span>
                      {preflight.estimated_aspect_ratio}
                    </div>
                  )}
                  <div className="text-muted-foreground">
                    {preflight.estimated_completion_window}
                  </div>
                </div>
                <ul className="space-y-1 text-[11px] text-muted-foreground">
                  <li>• Rendering can take several minutes.</li>
                  <li>• This consumes AI production capacity.</li>
                  <li>• The new video returns to Review — nothing auto-schedules or publishes.</li>
                  {preflight.projected_internal_cost_usd != null && (
                    <li>
                      • Projected internal spend: ${preflight.projected_internal_cost_usd.toFixed(2)}
                    </li>
                  )}
                </ul>
              </>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                data-testid="video-render-confirm-cancel"
                size="sm" variant="ghost"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </Button>
              {/* Patch 37.4.25 — Render Anyway path chains
                  accept-hero-risk → render-video as a single gesture. */}
              {preflight.hero_risk?.needs_risk_acceptance ? (
                <Button
                  data-testid="video-render-confirm-render-anyway"
                  size="sm"
                  variant="destructive"
                  disabled={
                    !preflight.eligible
                    || preflight.hero_risk.quality_level === "blocked"
                    || acceptRiskMut.isPending
                    || submitMut.isPending
                    || !preflight.hero_risk.hero_asset_id
                    || preflight.hero_risk.hero_asset_version == null
                  }
                  onClick={() => acceptRiskMut.mutate({
                    hero_asset_id: preflight.hero_risk!.hero_asset_id!,
                    hero_asset_version: preflight.hero_risk!.hero_asset_version!,
                  })}
                >
                  {acceptRiskMut.isPending || submitMut.isPending ? (
                    <>
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" /> Submitting…
                    </>
                  ) : (
                    "Render anyway"
                  )}
                </Button>
              ) : (
                <Button
                  data-testid="video-render-confirm-submit"
                  size="sm"
                  disabled={
                    !preflight.eligible
                    || preflight.hero_risk?.quality_level === "blocked"
                    || submitMut.isPending
                  }
                  onClick={() => submitMut.mutate(undefined)}
                >
                  {submitMut.isPending ? (
                    <>
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" /> Submitting…
                    </>
                  ) : (
                    "Render video"
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
